import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, Lock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import '../styles/Login.css';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [success, setSuccess] = useState(false);

  // Password validation
  const passwordChecks = {
    minLength: formData.password.length >= 8,
    hasUppercase: /[A-Z]/.test(formData.password),
    hasLowercase: /[a-z]/.test(formData.password),
    hasNumber: /[0-9]/.test(formData.password),
    passwordsMatch: formData.password === formData.confirmPassword && formData.confirmPassword !== ''
  };

  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/auth/verify-reset-token/${token}`);
        setTokenValid(true);
        setTokenEmail(response.data.email);
      } catch (error) {
        setTokenValid(false);
        toast.error(error.response?.data?.detail || 'Invalid or expired reset link');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPasswordValid) {
      toast.error('Please meet all password requirements');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password-with-token`, {
        token,
        new_password: formData.password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="login-page" data-testid="reset-password-page">
        <div className="login-container fade-in">
          <div className="login-brand">
            <div className="brand-content">
              <h1 className="brand-title" style={{ color: '#E7EDF9' }}>CNESS Hive</h1>
              <p className="brand-tagline">Verifying your reset link...</p>
            </div>
          </div>
          <div className="login-form-container">
            <div className="login-form-content" style={{ textAlign: 'center', padding: '60px 40px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 20px' }}></div>
              <p style={{ color: 'var(--text-secondary)' }}>Please wait while we verify your reset link...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invalid/missing token state
  if (!token || !tokenValid) {
    return (
      <div className="login-page" data-testid="reset-password-page">
        <div className="login-container fade-in">
          <div className="login-brand">
            <div className="brand-content">
              <h1 className="brand-title" style={{ color: '#E7EDF9' }}>CNESS Hive</h1>
              <p className="brand-tagline">Password Reset</p>
            </div>
          </div>
          <div className="login-form-container">
            <div className="login-form-content" style={{ textAlign: 'center' }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #f44336 0%, #e53935 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}>
                <XCircle size={40} color="white" />
              </div>
              
              <h2 style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>Invalid Reset Link</h2>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', lineHeight: '1.6' }}>
                This password reset link is invalid or has expired. 
                Please request a new one.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Link 
                  to="/forgot-password" 
                  className="gradient-btn"
                  style={{ 
                    textDecoration: 'none', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: '8px',
                    padding: '14px 24px'
                  }}
                >
                  Request New Link
                </Link>
                
                <Link 
                  to="/login"
                  style={{
                    color: 'var(--text-secondary)',
                    textDecoration: 'none',
                    fontSize: '14px'
                  }}
                >
                  ← Back to Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="login-page" data-testid="reset-password-page">
        <div className="login-container fade-in">
          <div className="login-brand">
            <div className="brand-content">
              <h1 className="brand-title" style={{ color: '#E7EDF9' }}>CNESS Hive</h1>
              <p className="brand-tagline">Password Reset Complete</p>
            </div>
          </div>
          <div className="login-form-container">
            <div className="login-form-content" style={{ textAlign: 'center' }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}>
                <CheckCircle size={40} color="white" />
              </div>
              
              <h2 style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>Password Reset Successful!</h2>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '32px', lineHeight: '1.6' }}>
                Your password has been changed successfully. 
                You can now log in with your new password.
              </p>

              <button
                onClick={() => navigate('/login')}
                className="gradient-btn"
                style={{ 
                  width: '100%',
                  padding: '14px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Lock size={18} />
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="login-page" data-testid="reset-password-page">
      <div className="login-container fade-in">
        <div className="login-brand">
          <div className="brand-content">
            <h1 className="brand-title" style={{ color: '#E7EDF9' }}>CNESS Hive</h1>
            <p className="brand-tagline">Create a new password for your account</p>
            <div className="brand-features">
              <div className="brand-feature">
                <div className="feature-dot"></div>
                <span>At least 8 characters</span>
              </div>
              <div className="brand-feature">
                <div className="feature-dot"></div>
                <span>Upper & lowercase letters</span>
              </div>
              <div className="brand-feature">
                <div className="feature-dot"></div>
                <span>At least one number</span>
              </div>
            </div>
          </div>
        </div>

        <div className="login-form-container">
          <div className="login-form-content">
            <div className="form-header">
              <h2>Reset Your Password</h2>
              <p>Enter a new password for <strong style={{ color: 'var(--color-primary)' }}>{tokenEmail}</strong></p>
            </div>

            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="password">New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Enter new password"
                    required
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="Confirm new password"
                    required
                    data-testid="reset-password-confirm"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Password Requirements */}
              <div style={{ 
                background: 'var(--bg-secondary)', 
                borderRadius: '8px', 
                padding: '16px',
                marginBottom: '20px'
              }}>
                <p style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)' }}>
                  Password Requirements:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {[
                    { key: 'minLength', label: 'At least 8 characters' },
                    { key: 'hasUppercase', label: 'One uppercase letter' },
                    { key: 'hasLowercase', label: 'One lowercase letter' },
                    { key: 'hasNumber', label: 'One number' },
                    { key: 'passwordsMatch', label: 'Passwords match' }
                  ].map(({ key, label }) => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {passwordChecks[key] ? (
                        <CheckCircle size={16} color="#4CAF50" />
                      ) : (
                        <AlertCircle size={16} color="var(--text-tertiary)" />
                      )}
                      <span style={{ 
                        fontSize: '13px', 
                        color: passwordChecks[key] ? '#4CAF50' : 'var(--text-tertiary)'
                      }}>
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                className="login-btn gradient-btn"
                disabled={loading || !isPasswordValid}
                data-testid="reset-password-submit"
                style={{ opacity: isPasswordValid ? 1 : 0.6 }}
              >
                {loading ? (
                  <span>Resetting...</span>
                ) : (
                  <>
                    <Lock size={18} />
                    <span>Reset Password</span>
                  </>
                )}
              </button>
            </form>

            <div className="form-footer">
              <Link to="/login" className="back-link">← Back to Login</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
